import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import { CurrencyDisplay } from "./CurrencySettings";
import { 
  Receipt, 
  Plus, 
  Edit2, 
  Trash2, 
  Settings,
  Store,
  Calendar
} from "lucide-react";

const PlatformDeductions = ({ restaurants = [] }) => {
  const [deductions, setDeductions] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [platformDialogOpen, setPlatformDialogOpen] = useState(false);
  const [editingDeduction, setEditingDeduction] = useState(null);
  const [newPlatformName, setNewPlatformName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Filters
  const [filterMonth, setFilterMonth] = useState("");
  const [filterRestaurant, setFilterRestaurant] = useState("all");
  const [filterPlatform, setFilterPlatform] = useState("all");
  
  const [formData, setFormData] = useState({
    restaurant_id: "",
    platform_id: "",
    month: new Date().toISOString().slice(0, 7), // YYYY-MM
    amount: "",
    notes: ""
  });

  useEffect(() => {
    fetchPlatforms();
    fetchDeductions();
  }, [filterMonth, filterRestaurant, filterPlatform]);

  const fetchPlatforms = async () => {
    try {
      const response = await axiosInstance.get("/platforms");
      setPlatforms(response.data);
    } catch (error) {
      console.error("Error fetching platforms:", error);
    }
  };

  const fetchDeductions = async () => {
    setLoading(true);
    try {
      let url = "/platform-deductions?";
      if (filterMonth) url += `month=${filterMonth}&`;
      if (filterRestaurant !== "all") url += `restaurant_id=${filterRestaurant}&`;
      if (filterPlatform !== "all") url += `platform_id=${filterPlatform}&`;
      
      const response = await axiosInstance.get(url);
      setDeductions(response.data);
    } catch (error) {
      toast.error("Failed to fetch deductions");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.restaurant_id || !formData.platform_id || !formData.month || !formData.amount) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      if (editingDeduction) {
        await axiosInstance.put(`/platform-deductions/${editingDeduction.id}`, {
          restaurant_id: formData.restaurant_id,
          platform_id: formData.platform_id,
          month: formData.month,
          amount: parseFloat(formData.amount),
          notes: formData.notes
        });
        toast.success("Deduction updated successfully");
      } else {
        await axiosInstance.post("/platform-deductions", {
          ...formData,
          amount: parseFloat(formData.amount)
        });
        toast.success("Deduction added successfully");
      }
      
      fetchDeductions();
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save deduction");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/platform-deductions/${id}`);
      toast.success("Deduction deleted");
      setDeleteTarget(null);
      fetchDeductions();
    } catch (error) {
      toast.error("Failed to delete deduction");
    }
  };

  const handleEdit = (deduction) => {
    setEditingDeduction(deduction);
    setFormData({
      restaurant_id: deduction.restaurant_id,
      platform_id: deduction.platform_id,
      month: deduction.month,
      amount: deduction.amount.toString(),
      notes: deduction.notes || ""
    });
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingDeduction(null);
    setFormData({
      restaurant_id: "",
      platform_id: "",
      month: new Date().toISOString().slice(0, 7),
      amount: "",
      notes: ""
    });
  };

  const handleAddPlatform = async () => {
    if (!newPlatformName.trim()) return;
    
    try {
      await axiosInstance.post("/platforms", { name: newPlatformName.trim() });
      toast.success("Platform added successfully");
      setNewPlatformName("");
      fetchPlatforms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add platform");
    }
  };

  const handleDeletePlatform = async (id) => {
    try {
      await axiosInstance.delete(`/platforms/${id}`);
      toast.success("Platform deleted");
      setDeleteTarget(null);
      fetchPlatforms();
    } catch (error) {
      toast.error("Failed to delete platform");
    }
  };

  // Calculate totals
  const totalDeductions = deductions.reduce((sum, d) => sum + d.amount, 0);
  
  // Group by platform for summary
  const platformTotals = deductions.reduce((acc, d) => {
    acc[d.platform_name] = (acc[d.platform_name] || 0) + d.amount;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="platform-deductions">
      {/* Header */}
      <Card className="shadow-lg border-0 bg-gradient-to-br from-red-50 to-orange-50">
        <CardHeader className="bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Receipt className="w-6 h-6" />
                Platform Deductions
              </CardTitle>
              <CardDescription className="text-red-100 mt-1">
                Manage monthly deductions from Zomato, Swiggy and other platforms
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="secondary" 
                size="sm"
                onClick={() => setPlatformDialogOpen(true)}
                className="gap-2"
                data-testid="manage-platforms-btn"
              >
                <Settings className="w-4 h-4" />
                Manage Platforms
              </Button>
              <Button 
                onClick={() => setDialogOpen(true)}
                className="gap-2 bg-white text-red-700 hover:bg-red-50"
                data-testid="add-deduction-btn"
              >
                <Plus className="w-4 h-4" />
                Add Deduction
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {/* Filters */}
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-1">
              <Label className="text-xs">Month</Label>
              <Input
                type="month"
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="cursor-pointer"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Restaurant</Label>
              <Select value={filterRestaurant} onValueChange={setFilterRestaurant}>
                <SelectTrigger>
                  <SelectValue placeholder="All Restaurants" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Restaurants</SelectItem>
                  {restaurants.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Platform</Label>
              <Select value={filterPlatform} onValueChange={setFilterPlatform}>
                <SelectTrigger>
                  <SelectValue placeholder="All Platforms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Platforms</SelectItem>
                  {platforms.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="bg-gradient-to-br from-red-50 to-orange-50 border-red-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Total Deductions</p>
            <p className="text-2xl font-bold text-red-700">
              <CurrencyDisplay amount={totalDeductions} />
            </p>
            <p className="text-xs text-gray-500 mt-1">{deductions.length} entries</p>
          </CardContent>
        </Card>
        {Object.entries(platformTotals).slice(0, 3).map(([platform, total]) => (
          <Card key={platform} className="bg-white">
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">{platform}</p>
              <p className="text-xl font-bold text-gray-900">
                <CurrencyDisplay amount={total} />
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Deductions Table */}
      <Card className="shadow-lg">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading deductions...</div>
          ) : deductions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Receipt className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No Deductions Found</p>
              <p className="text-sm mt-1">Add platform deductions to track your costs</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Month</TableHead>
                  <TableHead>Restaurant</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deductions.map((deduction) => (
                  <TableRow key={deduction.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {deduction.month ? (() => { const [y, m] = deduction.month.split('-'); const months = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return `${months[parseInt(m)]}-${y}`; })() : deduction.month}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Store className="w-4 h-4 text-gray-400" />
                        {deduction.restaurant_name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                        {deduction.platform_name}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-semibold text-red-600">
                      <CurrencyDisplay amount={deduction.amount} />
                    </TableCell>
                    <TableCell className="text-gray-500 text-sm max-w-[200px] truncate">
                      {deduction.notes || "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(deduction)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => setDeleteTarget({ type: 'deduction', id: deduction.id })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Deduction Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="deduction-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingDeduction ? "Edit Deduction" : "Add Platform Deduction"}
            </DialogTitle>
            <DialogDescription>
              Enter the monthly platform deduction details
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Restaurant *</Label>
              <Select
                value={formData.restaurant_id}
                onValueChange={(value) => setFormData({ ...formData, restaurant_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select restaurant" />
                </SelectTrigger>
                <SelectContent>
                  {restaurants.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Platform *</Label>
              <Select
                value={formData.platform_id}
                onValueChange={(value) => setFormData({ ...formData, platform_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent>
                  {platforms.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Month *</Label>
                <Input
                  type="month"
                  value={formData.month}
                  onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Additional notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit">
                {editingDeduction ? "Update" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Platforms Dialog */}
      <Dialog open={platformDialogOpen} onOpenChange={setPlatformDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Platforms</DialogTitle>
            <DialogDescription>
              Add or remove delivery platforms
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Add new platform */}
            <div className="flex gap-2">
              <Input
                placeholder="New platform name (e.g., Zomato)"
                value={newPlatformName}
                onChange={(e) => setNewPlatformName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddPlatform()}
              />
              <Button onClick={handleAddPlatform} disabled={!newPlatformName.trim()}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* Platform list */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {platforms.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No platforms added yet</p>
              ) : (
                platforms.map(platform => (
                  <div 
                    key={platform.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <span className="font-medium">{platform.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => setDeleteTarget({ type: 'platform', id: platform.id })}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPlatformDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === 'platform'
                ? "This will permanently delete this platform. This action cannot be undone."
                : "This will permanently delete this deduction entry. This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteTarget?.type === 'platform'
                ? handleDeletePlatform(deleteTarget.id)
                : handleDelete(deleteTarget.id)
              }
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PlatformDeductions;
